<template>
  <div
    ref="hostElement"
    :class="{
      'border-solid border-1 bg-white border-primary5 rounded-2': !isEditMode,
    }"
  >
    <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
    <template v-else>
      <EditableComponent
        v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
        :id="id"
        :active="componentEditMode && componentEditMode?.id === id"
        :duplicate="componentPermissions.canDuplicate"
        :edit="canEdit"
        :exclude-stylables="[ThemeComponentOptionEnum.COLOR_PICKER, ThemeComponentOptionEnum.PADDING]"
        :remove="componentPermissions.canRemove"
        :selected="componentSelectedMode && componentSelectedMode?.id === id"
        :settings="!usedInSectionId"
        stylable
        @duplicate="duplicate(id)"
        @edit="selectContent({ id, initialData: data })"
        @remove="removeComponentById(id)"
        @select="setComponentSelectedMode(id)"
        @settings="setComponentSettingsMode(id)"
        @style="emit('style', $event)"
      >
        <CollectionPlayerEmpty
          v-if="collectionData.groups.length === 0"
          @selectCollectionContent="selectContent({ id, initialData: data })"
        />

        <div v-else-if="needsFileResolution" class="flex items-center justify-center min-h-60">
          <CSpin :color="themeVars.primary" size="large" />
        </div>

        <CollectionPlayerApp
          v-else
          :id="id"
          :key="`collection-player-${id}-${forceRerenderKey}`"
          ref="app"
          :api="api"
          :aspect-ratio="aspectRatio"
          :data="collectionData"
          :is-edit-mode="isEditMode"
          :style="[style, heightStyle, zIndexStyle]"
          @click="setComponentSelectedMode(id)"
        />
      </EditableComponent>

      <template v-else>
        <div v-if="isPrintDisplayMode" class="ma-4">
          <div v-for="group in collectionData.groups" :key="group.id" class="break-after-page text-center">
            <div
              class="block overflow-hidden font-normal text-[20px] leading-8 tracking-wide text-ellipsis whitespace-nowrap text-left mb-4"
            >
              {{ group.name }}
            </div>

            <template v-for="item in group.slides" :key="item.id">
              <img v-if="'slide' in item && item.slide.url" class="mb-4" :src="item.slide.url" />

              <img
                v-else-if="item.file.content_type === FileContentTypeEnum.IMAGE && item.file.content_url"
                class="mb-4"
                :src="item.file.content_url"
              />
            </template>
          </div>
        </div>

        <div v-else-if="needsFileResolution" class="flex items-center justify-center min-h-60">
          <CSpin :color="themeVars.primary" size="large" />
        </div>

        <CollectionPlayerApp
          v-else
          :id="id"
          :key="`collection-player-${id}-${forceRerenderKey}`"
          ref="app"
          :api="api"
          :aspect-ratio="aspectRatio"
          :data="collectionData"
          :style="[style, heightStyle, zIndexStyle]"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { get } from 'lodash-es'
import {
  toRefs,
  inject,
  ref,
  computed,
  type Ref,
  watch,
  useTemplateRef,
  onBeforeUnmount,
  type ComputedRef,
  onMounted,
  nextTick,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { type CanvasRetrieve, FileContentTypeEnum } from '@openapi'
import type { ComponentNode, CollectionFileEntity } from '@canvas-builder/types/canvas'
import type {
  CollectionPlayer,
  CollectionPlayerV2,
  CollectionGroup,
  CollectionSlideV2,
} from '@collection-player/types/collection-player.type'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import CollectionPlayerEmpty from './CollectionPlayerEmpty.vue'
import CollectionPlayerApp from '@collection-player/App.vue'
import useCollectionSelector from '@canvas-builder/composables/useCollectionSelector'
import type { UiSelectCollectionPlayerRequest, UiSelectCollectionPlayerResponse } from '@sdk/api/modules/ui/types.ui'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import type { Api as CollectionPlayerApi } from '@collection-player/stores/app.store'
import type { ActionCallback } from '@collection-player/composables/keyboard-bindings.use.ts'
import { getImageSize } from '@lib/util/image.ts'
import useToast from '@lib/composables/useToast'
import { transformFilesToCollectionPlayer } from '@collection-selector/utils/content-selector-adapter.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useLegacyCollectionFileLoader from '@lib/composables/useLegacyCollectionFileLoader'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import useThemeVars from '@lib/composables/useThemeVars'

const FULLSCREEN_SAFE_MARGIN = '64px'
const PRESENTATION_SAFE_MARGIN = '32px'
const HEADER_HEIGHT = '116px'
const SHARING_SAFE_MARGIN = '10px'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: CollectionPlayer
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: Record<string, any>
    height?: string
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: Record<string, any>]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { toast } = useToast()
const { t } = useI18n()
const themeVars = useThemeVars()

const {
  mode,
  isEditMode,
  isTemplate,
  removeComponentById,
  componentEditMode,
  setComponentSelectedMode,
  componentSelectedMode,
  duplicate,
  isViewOnlyMode,
  setComponentSettingsMode,
  renderPageAsImageUrl,
  onFileEnter,
  onFileExit,
  onFilePageEnter,
  onFilePageExit,
  isPrintDisplayMode,
  visibleCollectionPlayerRefs,
  activeCanvas,
  isDsr,
  updateNodeDataById,
  saveCanvasContent,
} = useCanvas()

const app = useTemplateRef<{ isFullscreen: Ref<boolean>; handleKeyAction: ActionCallback; element: HTMLElement }>('app')
const aspectRatio = ref<number | undefined>(undefined)
const forceRerenderKey = ref(0)

// Use legacy collection file loader for backward compatibility with old format collections
const fileLoader = useLegacyCollectionFileLoader()

const collectionDataResult = computed<{ data: CollectionPlayer | CollectionPlayerV2; error?: string | null }>(() => {
  if (isTemplate.value || !launchDarkly.value.enable_collection_player_data_accessor) {
    return { data: props.data }
  }

  if (!props.data.data_accessor) {
    return { data: props.data }
  }

  try {
    const contextData = get(activeCanvas.value?.context, props.data.data_accessor)

    if (!contextData) {
      throw new Error()
    }

    const transformedData = transformFilesToCollectionPlayer(t, contextData, props.data.name || 'Collection')

    return {
      data: {
        ...transformedData,
        hide_footer: props.data.hide_footer,
        data_accessor: props.data.data_accessor,
      } as CollectionPlayerV2,
      error: null,
    }
  } catch (error) {
    return {
      data: props.data,
      error: t('canvasUI.canvasBuilder.collectionPlayer.errorAccessingData', { path: props.data.data_accessor }),
    }
  }
})

// Load files using the new file loader
const loadFiles = async (rawData: any): Promise<void> => {
  if (!rawData?.groups) {
    return
  }

  // Check if this is V2 format data that needs file resolution
  const isV2Format = rawData.groups.every(
    (group: any) =>
      Array.isArray(group?.slides) && group.slides.every((slide: any) => 'file_id' in slide && !slide.file),
  )

  if (!isV2Format) {
    return
  }

  // Extract file IDs that need loading
  const slides = rawData.groups.flatMap((group: any) => group.slides ?? [])
  const ids: string[] = Array.from(
    new Set<string>(
      slides.map((slide: any) => slide.file_id).filter((id: any) => id && typeof id === 'string' && id !== 'undefined'),
    ),
  )

  if (ids.length === 0) {
    return
  }

  // Initialize files in the loader
  fileLoader.initializeFiles(ids)

  // Load all files immediately
  await fileLoader.loadAllFiles(ids)

  // Force a re-render after files are loaded
  forceRerenderKey.value++
}

// Check if we need to wait for file loading
const needsFileResolution = computed(() => {
  const rawData = collectionDataResult.value.data

  if (!rawData.groups || rawData.groups.length === 0) {
    return false
  }

  // Check if this is V2 format with unloaded files
  const isV2Format = rawData.groups.every(
    (group: any) =>
      Array.isArray(group?.slides) && group.slides.every((slide: any) => 'file_id' in slide && !slide.file),
  )

  if (!isV2Format) {
    return false // Already V1 format, no loading needed
  }

  // Check if we have all required files loaded or resolved (failed)
  const allFileIds = rawData.groups.flatMap((group: any) =>
    group.slides.map((slide: any) => slide.file_id).filter(Boolean),
  )

  // Show loading if any files aren't resolved yet (either loaded or failed)
  return allFileIds.some((fileId) => !fileLoader.isFileResolved(fileId))
})

// Transform V2 data with loaded files back to V1 format for rendering
const collectionData = computed<CollectionPlayer>(() => {
  const rawData = collectionDataResult.value.data

  // If it's already V1 format or no loaded files available, return as-is
  if (!rawData.groups || rawData.groups.length === 0) {
    return rawData
  }

  // Check if this is V2 format (has file_id instead of file objects)
  const isV2Format = rawData.groups.every(
    (group: any) =>
      Array.isArray(group?.slides) && group.slides.every((slide: any) => 'file_id' in slide && !slide.file),
  )

  if (!isV2Format) {
    return rawData // Already V1 format
  }

  // Transform V2 back to V1 format using loaded file data
  const transformedGroups = rawData.groups.map((group: any) => ({
    id: group.id,
    name: group.name,
    slides: group.slides.flatMap((slide: any) => {
      const loadedFile = fileLoader.getFile(slide.file_id)

      // Skip slides with failed files (404, deleted, etc.)
      if (!loadedFile && fileLoader.hasFileFailed(slide.file_id)) {
        return []
      }

      if (!loadedFile) {
        // File not loaded yet, return minimal structure with loading name if available
        const fileName = fileLoader.getFileName(slide.file_id)
        return {
          id: slide.id,
          type: slide.index !== undefined ? 'slide' : 'file',
          file: {
            id: slide.file_id,
            name: fileName || '',
          },
          ...(slide.index !== undefined && {
            slide: { index: slide.index, url: null },
          }),
        }
      }

      // Transform FileRetrieve to CollectionFileEntity
      const collectionFile: CollectionFileEntity = {
        id: loadedFile.id,
        name: loadedFile.name,
        type: loadedFile.type,
        tags: loadedFile.tags,
        metadata: loadedFile.metadata,
        thumbnail_url: loadedFile.thumbnail_url,
        permissions: loadedFile.permissions,
        content_url: loadedFile.content_url,
        content_type: loadedFile.content_type,
        pspdfkit_auth_payload: loadedFile.pspdfkit_auth_payload,
        pspdfkit_document_id: loadedFile.pspdfkit_document_id,
        pspdfkit_server_url: loadedFile.pspdfkit_server_url,
      }

      if (slide.index !== undefined) {
        // This is a specific slide (page from PDF)
        const slideUrl = loadedFile.content_thumbnails?.[slide.index] || loadedFile.thumbnail_url || null

        return {
          id: slide.id,
          type: 'slide' as const,
          file: collectionFile,
          slide: {
            index: slide.index,
            url: slideUrl,
          },
        }
      } else {
        // This is an entire file - expand to all pages if it's a PDF
        if (loadedFile.content_type === 'pdf' && loadedFile.content_thumbnails?.length > 0) {
          // Expand to multiple slides (one for each page)
          return loadedFile.content_thumbnails.map((thumbnailUrl, pageIndex) => ({
            id: `${slide.id}-page-${pageIndex}`,
            type: 'slide' as const,
            file: collectionFile,
            slide: {
              index: pageIndex,
              url: thumbnailUrl,
            },
          }))
        } else {
          // Single file (image, video, etc.)
          return {
            id: slide.id,
            type: 'file' as const,
            file: collectionFile,
          }
        }
      }
    }),
  }))

  return {
    ...rawData,
    groups: transformedGroups,
  }
})

watch(
  () => collectionDataResult.value.error,
  (newError, oldError) => {
    if (newError && newError !== oldError) {
      toast.error(newError)
    }
  },
)

// Watch for changes in collectionData and update the store when files are loaded
watch(
  collectionData,
  (newData) => {
    // Only update store if we have loaded file data and the app is mounted
    if (app.value && newData && Object.keys(fileLoader.filesById.value).length > 0) {
      updateCollectionPlayerStore()
    }
  },
  { deep: true },
)

const firstSlide = computed(() => collectionData.value.groups[0]?.slides?.[0] ?? null)

watch(
  firstSlide,
  async (newItem, oldItem) => {
    if (!newItem) {
      return
    }

    const oldImageUrl = oldItem ? ('slide' in oldItem ? oldItem.slide.url : oldItem.file?.thumbnail_url) : undefined
    const imageUrl = 'slide' in newItem ? newItem.slide.url : newItem.file?.thumbnail_url

    if (!imageUrl || oldImageUrl === imageUrl) {
      return
    }

    const { height, width } = await getImageSize(imageUrl)

    aspectRatio.value = width / height
  },
  { immediate: true },
)

const api: CollectionPlayerApi = {
  renderPageAsImage: renderPageAsImageUrl.value as CollectionPlayerApi['renderPageAsImage'],
  onFileEnter: onFileEnter.value,
  onFileExit: onFileExit.value,
  onFilePageEnter: onFilePageEnter.value,
  onFilePageExit: onFilePageExit.value,
}

const hostElement = ref<HTMLElement | null>(null)
const { id, usedInSectionId, when_used_in_section, data } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const canEdit = computed(
  () =>
    componentPermissions.value.canEdit &&
    (!launchDarkly.value.enable_collection_player_data_accessor || !data.value.data_accessor),
)

const zIndexStyle = computed(() => (app.value?.isFullscreen ? { zIndex: 1000000 } : {}))

const heightStyle = computed(() => {
  if (app.value?.isFullscreen) {
    return {
      height: isDsr.value ? `calc(100vh - ${SHARING_SAFE_MARGIN})` : `calc(100vh - ${FULLSCREEN_SAFE_MARGIN})`,
    }
  }

  let height: undefined | string = undefined

  if (props.height) {
    height = props.height.replaceAll(' ', '')
  } else if (isEditMode.value) {
    height = '60vh'
  } else {
    height = isDsr.value
      ? `calc(100vh - ${SHARING_SAFE_MARGIN})`
      : `calc(100vh - ${PRESENTATION_SAFE_MARGIN} - ${HEADER_HEIGHT})`
  }

  return {
    height: props.height ? height : undefined,
    maxHeight: height,
    width: '100%',
  }
})

const selectCollectionPlayerContent = inject<
  (payload?: UiSelectCollectionPlayerRequest) => Promise<UiSelectCollectionPlayerResponse>
>('selectCollectionPlayerContent')

const { selectContent } = useCollectionSelector(selectCollectionPlayerContent)

// Single watcher to handle file loading when data changes
// Use a computed to create a stable reference that doesn't change with edit mode
const dataForFileLoading = computed(() => {
  const data = collectionDataResult.value.data
  if (!data) return null

  // Create a stable key for the data that doesn't include mode-specific changes
  return {
    groups: data.groups,
    name: data.name,
    data_accessor: (data as any).data_accessor,
  }
})

watch(
  dataForFileLoading,
  async (newData) => {
    if (newData) {
      await loadFiles(newData)
    }
  },
  { deep: true, immediate: true },
)

// Function to update the collection player store with loaded data
const updateCollectionPlayerStore = () => {
  // Use the already computed collectionData which has the transformed V1 format
  const loadedData = collectionData.value

  // Force a re-render by incrementing the key
  forceRerenderKey.value++

  // Wait for next tick to ensure the component is re-rendered with new key
  nextTick(() => {
    // Get the app instance and update its store
    if (app.value && loadedData) {
      // The CollectionPlayerApp component should expose a method to update its store
      if ('updateResolvedData' in app.value) {
        ;(app.value as any).updateResolvedData(loadedData)
      }
    }
  })
}

watch(
  app,
  () => {
    if (!visibleCollectionPlayerRefs.value.has(app)) {
      visibleCollectionPlayerRefs.value.add(app)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  visibleCollectionPlayerRefs.value.delete(app)
})

// Handle V1 to V2 migration on mount
onMounted(async () => {
  // Use raw data from collectionDataResult, not the transformed collectionData
  const rawData = collectionDataResult.value.data as any

  // Check if data is already in V2 format (slides have file_id directly)
  const isV2 =
    Array.isArray(rawData?.groups) &&
    rawData.groups.every(
      (group: any) => Array.isArray(group?.slides) && group.slides.every((slide: any) => 'file_id' in slide),
    )

  // Migrate V1 to V2 format if needed
  if (!isV2 && Array.isArray(rawData?.groups)) {
    try {
      const migratedGroups = (rawData.groups as CollectionGroup[]).map((group) => ({
        id: group.id,
        name: group.name,
        slides: group.slides
          .map(
            (slide): CollectionSlideV2 => ({
              id: slide.id,
              file_id: slide.file?.id || '',
              index: slide.type === 'slide' ? slide.slide.index : undefined,
            }),
          )
          .filter((slide) => !!slide.file_id),
      }))

      const newData: CollectionPlayerV2 = {
        ...rawData,
        groups: migratedGroups,
      }

      updateNodeDataById(id.value, newData)
      await saveCanvasContent({ showToast: false })
    } catch (error) {
      console.warn('[CollectionPlayer] Migration to V2 schema failed', error)
    }
  }

  // File resolution is handled by the watcher, no need to duplicate here
})
</script>

<style scoped lang="scss">
:deep(.n-dropdown-option) {
  padding: 4px 0 !important;
}

:deep(.n-dropdown-option-body) {
  padding-left: 8px !important;
  padding-right: 8px !important;

  &:before {
    left: 0 !important;
    right: 0 !important;
  }
}
</style>
